import { apiClient } from '../../services/client';
import { IUser } from '../../@shared/interfaces';
import { API_ENDPOINTS } from '../../services/endpoints';

interface ApiResponse<T> {
  success: boolean;
  user: T;
  message?: string;
}

interface SimpleApiResponse {
  success: boolean;
  message?: string;
}

export interface RegisterUserData {
  auth0Uid: string;
  email: string;
  firstName: string;
  lastName: string;
  photoUrl?: string;
  provider: string;
}

export interface UpdateNameData {
  firstName: string;
  lastName: string;
}

export interface PhoneVerificationData {
  mobile: string;
}

export interface VerifyOTPData {
  otpCode: string;
}

export interface BillingPortalResponse {
  success: boolean;
  customerClientSecret?: string;
  message?: string;
}

export interface UpdateUserTimeZoneData {
  timeZoneName: string;
  timeZoneOffset?: number;
}

export const userService = {
  async getProfile(): Promise<ApiResponse<IUser>> {
    const response = await apiClient.get(API_ENDPOINTS.PROFILE);
    return response.data;
  },

  async updateProfile(data: Partial<IUser>): Promise<ApiResponse<IUser>> {
    const response = await apiClient.put(API_ENDPOINTS.UPDATE_PROFILE, data);
    return response.data;
  },

  async getSettings(): Promise<ApiResponse<IUser['settings']>> {
    const response = await apiClient.get(API_ENDPOINTS.SETTINGS);
    return response.data;
  },

  async checkUser(
    email: string,
    auth0Uid: string
  ): Promise<{ success: boolean; existingUser: boolean; message?: string }> {
    const response = await apiClient.post(API_ENDPOINTS.CHECK_USER, {
      email,
      auth0Uid,
    });
    return response.data;
  },

  async registerUser(data: RegisterUserData): Promise<ApiResponse<IUser>> {
    const response = await apiClient.post(API_ENDPOINTS.REGISTER_USER, data);
    return response.data;
  },

  async getGoogleAuthUrl(): Promise<{ authUrl: string }> {
    const response = await apiClient.get(API_ENDPOINTS.GOOGLE_AUTH_URL);
    return response.data;
  },

  // Phone Verification Methods
  async generateOTP(data: PhoneVerificationData): Promise<SimpleApiResponse> {
    const response = await apiClient.post(API_ENDPOINTS.GENERATE_OTP, data);
    return response.data;
  },

  async verifyOTP(data: VerifyOTPData): Promise<ApiResponse<IUser>> {
    const response = await apiClient.post(API_ENDPOINTS.VERIFY_OTP, data);
    return response.data;
  },

  async unverifyPhone(): Promise<SimpleApiResponse> {
    const response = await apiClient.post(API_ENDPOINTS.UNVERIFY_PHONE);
    return response.data;
  },

  // Billing Methods
  async generateCustomerSecret(): Promise<BillingPortalResponse> {
    const response = await apiClient.post(
      API_ENDPOINTS.GENERATE_CUSTOMER_SECRET
    );
    return response.data;
  },

  async updateUserTimeZone(
    data: UpdateUserTimeZoneData
  ): Promise<SimpleApiResponse> {
    const response = await apiClient.patch(
      API_ENDPOINTS.UPDATE_USER_TIMEZONE,
      data
    );
    return response.data;
  },

  // Utility Methods
  getReferralLink(referralCode: string): string {
    const baseUrl = window.location.origin;
    return `${baseUrl}?ref=${referralCode}`;
  },
};
